import React, { useState, useEffect, useCallback } from 'react';
import dayjs from 'dayjs';

import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import Chip from '@mui/material/Chip';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';

import CampaignPicker, { STATUS_LABEL } from './CampaignPicker.js';
import { smGet, smPost, errorText, personName } from './api.js';

const SERVER_NAME = { production: 'production (app.monada.ai)', alpha: 'ALPHA', local: 'a developer\'s machine' };

function Check({ ok, children }) {
    return <Typography variant="body2" sx={{ mb: 0.5 }}>{ok ? '✅' : '⬜️'} {children}</Typography>;
}

function LaunchTab({ campaignId, settings, onCampaign }) {
    const [data, setData] = useState(null);
    const [busy, setBusy] = useState(false);
    const [error, setError] = useState(null);
    const [confirm, setConfirm] = useState(false);
    const [refreshKey, setRefreshKey] = useState(0);

    const load = useCallback(async () => {
        if (!campaignId) { setData(null); return; }
        try { setData(await smGet(`/campaigns/${campaignId}`)); } catch (e) { setError(errorText(e)); }
    }, [campaignId]);
    useEffect(() => { load(); }, [load]);
    // While it runs, the worker sends and reads replies: refresh every minute.
    useEffect(() => {
        if (data?.campaign?.status !== 'running') return undefined;
        const t = setInterval(load, 60 * 1000);
        return () => clearInterval(t);
    }, [data, load]);

    const run = async fn => {
        setBusy(true); setError(null);
        try { await fn(); await load(); setRefreshKey(k => k + 1); } catch (e) { setError(errorText(e)); }
        setBusy(false);
    };

    const campaign = data?.campaign;
    const events = data?.events || { sends: [], errors: [] };
    const approved = Object.values(campaign?.messages || {}).filter(m => m.status === 'approved').length;
    const mailbox = settings?.mailbox;
    const contact = id => (campaign?.contacts || []).find(c => c.id === id) || {};
    const days = settings?.weekdaysOnly ? (settings?.timezone === 'Asia/Jerusalem' ? 'Sunday to Thursday' : 'Monday to Friday') : 'every day';
    const totalEmails = approved * (campaign?.steps?.length || 0);

    return (
        <Box>
            <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', mb: 2 }}>
                <CampaignPicker value={campaignId} onChange={onCampaign} refreshKey={refreshKey} />
                {campaign && <Chip label={STATUS_LABEL[campaign.status] || campaign.status} color={campaign.status === 'running' ? 'success' : 'default'} />}
            </Box>
            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
            {!campaign && <Typography color="text.secondary">Choose a campaign.</Typography>}
            {campaign && settings && (
                <>
                    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
                        <Typography variant="subtitle2" sx={{ mb: 1 }}>Before launching</Typography>
                        <Check ok={!!mailbox?.connected}>{mailbox?.connected ? <>Sends from <b>{mailbox.fromName ? `${mailbox.fromName} <${mailbox.address}>` : mailbox.address}</b></> : 'Connect a mailbox in Settings'}</Check>
                        <Check ok={approved > 0}>{approved} of {(campaign.contacts || []).length} people approved; the others are not sent anything</Check>
                        <Check ok>{campaign.steps.length} emails each: {campaign.steps.map((s, i) => (i ? `${s.label} after ${s.delayDays} days` : s.label)).join(', ')}. A reply, a bounce or an opt-out stops a person's emails.</Check>
                        <Check ok>Up to {settings.dailyLimit} emails a day, {days}, {settings.hours.start}:00 to {settings.hours.end}:00 ({settings.timezone}), one at a time about every 5 minutes. About {totalEmails} emails in all.</Check>
                        <Check ok={settings.runsOn === settings.thisServer}>
                            Sent by {SERVER_NAME[settings.runsOn] || settings.runsOn}{settings.runsOn !== settings.thisServer ? ` (you are on ${SERVER_NAME[settings.thisServer] || settings.thisServer}: launching here is fine, the emails go out from ${SERVER_NAME[settings.runsOn] || settings.runsOn})` : ''}
                        </Check>
                        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
                            {['draft', 'paused'].includes(campaign.status) && (
                                <Button variant="contained" color="success" onClick={() => setConfirm(true)} disabled={busy || !approved || !mailbox?.connected}>
                                    {campaign.status === 'paused' ? 'Resume the campaign' : 'Launch the campaign'}
                                </Button>
                            )}
                            {campaign.status === 'running' && <Button variant="outlined" color="warning" onClick={() => run(() => smPost(`/campaigns/${campaignId}/pause`))} disabled={busy}>Pause</Button>}
                        </Box>
                        {campaign.launchedAt && <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>Launched {dayjs(campaign.launchedAt).format('D MMM YYYY, HH:mm')}{events.lastReplyCheck ? ` · inbox last checked ${dayjs(events.lastReplyCheck).format('D MMM, HH:mm')}` : ''}</Typography>}
                    </Paper>

                    {events.errors?.length > 0 && (
                        <Alert severity="error" sx={{ mb: 2 }}>
                            {events.errors.length} email{events.errors.length === 1 ? '' : 's'} failed to send. The last: {events.errors[events.errors.length - 1].error}
                        </Alert>
                    )}

                    <Paper variant="outlined">
                        <Typography variant="subtitle2" sx={{ p: 1.5 }}>Sent ({events.sends.length})</Typography>
                        <Table size="small">
                            <TableHead><TableRow><TableCell>When</TableCell><TableCell>To</TableCell><TableCell>Company</TableCell><TableCell>Email</TableCell><TableCell>Subject</TableCell></TableRow></TableHead>
                            <TableBody>
                                {[...events.sends].reverse().slice(0, 200).map((s, i) => (
                                    <TableRow key={i}>
                                        <TableCell>{dayjs(s.at).format('D MMM, HH:mm')}</TableCell>
                                        <TableCell>{personName(contact(s.contactId))}</TableCell>
                                        <TableCell>{contact(s.contactId).company}</TableCell>
                                        <TableCell>{campaign.steps[s.step]?.label}</TableCell>
                                        <TableCell>{s.subject}</TableCell>
                                    </TableRow>
                                ))}
                                {!events.sends.length && <TableRow><TableCell colSpan={5}><Typography variant="body2" color="text.secondary">Nothing sent yet.</Typography></TableCell></TableRow>}
                            </TableBody>
                        </Table>
                    </Paper>
                </>
            )}

            <Dialog open={confirm} onClose={() => setConfirm(false)}>
                <DialogTitle>{campaign?.status === 'paused' ? 'Resume' : 'Launch'} "{campaign?.name}"?</DialogTitle>
                <DialogContent>
                    <Typography>
                        {approved} people will be emailed from {mailbox?.address}, up to {settings?.dailyLimit} emails a day, with follow-ups until they reply. You can pause at any time.
                    </Typography>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setConfirm(false)}>Cancel</Button>
                    <Button variant="contained" color="success" onClick={() => { setConfirm(false); run(() => smPost(`/campaigns/${campaignId}/launch`)); }}>
                        {campaign?.status === 'paused' ? 'Resume' : 'Launch'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}

export default LaunchTab;
